import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array, Callback, Dict} from "../core/Reflection";
import {Times} from "../core/Signal";
import {Assets} from "../data/Assets";
import {Environment} from "../data/Environment";
import {DefaultMatch2screenData, IMatch2ScreenData} from "../data/IMatch2ScreenData";
import {LocationPostcardLayout} from "../data/Location";
import {CardNumber, isCardExcluded, LocationCardType, MaxCardNumber, MinCardNumber} from "../data/LocationCardType";
import {Match2Model} from "../data/Match2Model";
import {OnboardingStep} from "../data/OnboardingStep";
import {PersistentGameModel} from "../data/PersistentGameModel";
import {
    getLocationFactVO,
    getLocationIdleVO,
    getLocationNameVO,
    getLocationPostcardVO,
    getLocationTileVO,
    voMatchKudos,
    voOptionsDirections,
    voPostcardCompleteKudos,
} from "../data/VoiceOver";
import {Button, ButtonHome, ButtonLoading} from "../gameobjects/Button";
import {ButtonMute} from "../gameobjects/ButtonMute";
import {IdleVO} from "../gameobjects/IdleVO";
import {Keyboard} from "../gameobjects/Keyboard";
import {LocationBackground} from "../gameobjects/LocationBackground";
import {Luna} from "../gameobjects/Luna";
import {Match2Board} from "../gameobjects/Match2Board";
import {Match2Card} from "../gameobjects/Match2Card";
import {OnboardingContainer} from "../gameobjects/OnboardingContainer";
import {Postcard} from "../gameobjects/Postcard";
import {PrintablePostcard} from "../gameobjects/PrintablePostcard";
import {ITransformBackup} from "../gameobjects/TransformBackup";
import {VOWatcher} from "../gameobjects/VOWatcher";
import {BoardCardsLayout_main} from "../layout/BoardCardsLayout";
import {GameScreenLayout_main} from "../layout/GameScreenLayout";
import {LocationBackgroundLayout_main} from "../layout/LocationBackgroundLayout";
import {Analytics} from "../managers/Analytics";
import {AudioChannel} from "../managers/AudioChannel";
import {CaptionManager} from "../managers/CaptionManager";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {AbstractGameState} from "./AbstractGameState";
import {SelectLocationScreen} from "./SelectLocationScreen";

export class GameScreen extends AbstractGameState {

    public static readonly NAME = "GameScreen";

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new LocationBackground());
    public readonly buttonHome = this.addChild(new ButtonHome("button-home-round", () => {
        this.gotoNextState(SelectLocationScreen.NAME, {...this._screenData});
    }));
    public readonly buttonNext = this.addChild(new Button("button-next", () => {
        this.board.visible = false;
        this._isMatchVoComplete = true;
        this.showLuna();
        this.onRevealPostcard();
    }));
    public readonly postcardContainer = this.main.addChild(new Container());
    public readonly completePostcardContainer = this.main.addChild(new Container());
    public readonly postcardEnd = new (class extends Container {
        public readonly background = {};
    })();
    public luna!: Luna;
    public readonly buttonComplete = this.completePostcardContainer.addChild(new ButtonHome(undefined, () => {
        this.gotoNextState(SelectLocationScreen.NAME, {...this._screenData});
    }));
    public readonly buttonSave = this.completePostcardContainer.addChild(new ButtonLoading(undefined, () => {
        if (this._printablePostcard) {
            this._printablePostcard.save();
        }
    }));
    public readonly buttonPrint = this.completePostcardContainer.addChild(new ButtonLoading(undefined, () => {
        if (this._printablePostcard) {
            this._printablePostcard.print();
        }
    }));
    public readonly buttonMute = this.addChild(new ButtonMute());
    public readonly onboardingContainer = this.addChild(new OnboardingContainer());

    public postcard!: Postcard;
    public board!: Match2Board<LocationCardType>;

    private _printablePostcard?: PrintablePostcard;
    private _isMatchVoComplete = false;
    private _isPostcardRevealed = false;
    private _isPostcardRendered = false;
    private _isPostcardComplete = false;
    private _screenData!: IMatch2ScreenData;
    private _idleVO!: IdleVO;
    private readonly _idleVOOneMath = new IdleVO("patw_g07", "patw_g06");
    private readonly _idleVOComplete = new IdleVO(...(Environment.isBPS() ?
        ["patw_o05", "patw_g12"] :
        ["patw_o04", "patw_o05", "patw_g12"]));

    private readonly _voWatchers: Dict<string, VOWatcher> = {
        patw_o04: new VOWatcher("patw_o04", 1, () => {
            this.buttonSave.pulse(1.2, 0, true);
            this.buttonPrint.pulse(1.2, 0, true);
        }, () => {
            this.buttonSave.stopPulse(false);
            this.buttonPrint.stopPulse(false);
        }),
        patw_o05: new VOWatcher("patw_o05", 1, () => {
            this.buttonComplete.pulse(1.2, 0, true);
        }, () => {
            this.buttonComplete.stopPulse(false);
        }),
        patw_g12: new VOWatcher("patw_g12", 1, () => {
            this.postcard.flashItems();
        }, () => {
            this.postcard.stopFlashItems();
        }),
    };

    public init(pSize: PIXI.Point, pData?: IMatch2ScreenData) {
        super.init(pSize);

        this._screenData = pData ?? {...DefaultMatch2screenData};

        this.main.scale.set(0.5);

        LocationBackgroundLayout_main.call(this);
        this.background.setLocation(this._screenData.location);
        this.background.zoomIn();

        this.completePostcardContainer.visible = false;

        this.buttonHome.visible = false;
        this.newPostcard(() => {
            this.buttonHome.visible = true;
            this.buttonHome.moveYFrom(-this._size.y / 2 - this.buttonHome.height, 0.4, Ease.backOut);
            Keyboard.add(
                this.buttonHome,
                ...this.board!.cards,
                this.buttonMute,
                this.buttonNext,
            );
        });

        this.buttonHome.hoverVO = "patw_ui15";
        this.buttonComplete.hoverVO = "patw_ui15";
        this.buttonPrint.hoverVO = "patw_ui16";
        this.buttonSave.hoverVO = "patw_ui17";

        const locationIdle = getLocationIdleVO(this._screenData.location);
        this._idleVO = new IdleVO(
            "patw_g02",
            ...(locationIdle[0] ? [locationIdle[0]] : []),
            "patw_g03",
            "patw_ui07",
            ...(locationIdle[1] ? [locationIdle[1]] : []),
            "patw_g01",
            "patw_ui09",
            ["patw_ui09b", getLocationNameVO(this._screenData.location)],
        );
        this._idleVOComplete.onVOStart.add((vo) => {
            if (typeof vo === "string") {
                const watcher = this._voWatchers[vo];
                if (watcher) {
                    watcher.start();
                }
            }
        });

        if (Environment.isLive()) {
            this.buttonNext.visible = false;
        }

        VoiceOverManager.instance.playVO([
            getLocationPostcardVO(this._screenData.location, this._screenData.level),
            ...(PersistentGameModel.getOnboardingStep() < OnboardingStep.ChooseTile ? [] : [0.25, "patw_ui09"]),
        ], {
            ...voOptionsDirections,
            callback: () => {
                this.onboardingChooseTile();
            },
        });

        Analytics.event("startLevel", `${this._screenData.location}-${this._screenData.level}`);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        const p = 6;
        this.buttonHome.position.set(
            -this._size.x / 2 + this.buttonHome.width * 0.5 + p,
            -this._size.y / 2 + this.buttonHome.height * 1.15);

        this.buttonNext.position.set(
            this._size.x / 2 - this.buttonNext.width * 0.5 - p,
            this._size.y / 2 - this.buttonNext.height * 0.5 - p);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions) {
        this.stopIdleInterval();
        for (const p in this._voWatchers) {
            if (this._voWatchers.hasOwnProperty(p)) {
                (this._voWatchers[p] as VOWatcher).destroy();
            }
        }
        if (this._printablePostcard) {
            this._printablePostcard.destroy();
        }
        this._idleVO.destroy();
        this._idleVOOneMath.destroy();
        this._idleVOComplete.destroy();
        super.destroy(pOptions);
    }

    private onboardingChooseTile() {
        const card = this.board.cards[0];
        this.onboardingContainer.showOnboardingStep(OnboardingStep.ChooseTile, this.board, [
            {x: card.x - card.width * 0.8, y: card.y - card.height * 0.1, d: "right"},
        ], () => {
            this.startIdleInterval();
        });
    }

    private onboardingPostcardComplete() {
        if (Environment.isBPS()) {
            this.onboardingGoHome();
        } else {
            this.onboardingSavePrint();
        }
    }

    private onboardingSavePrint() {
        this.onboardingContainer.showOnboardingStep(OnboardingStep.SavePrint, this.main, [
            {x: (this.buttonSave.x + this.buttonPrint.x) / 2, y: this.buttonSave.y - this.buttonSave.height, d: "down"},
        ], () => this.onboardingGoHome());
    }

    private onboardingGoHome() {
        this.onboardingContainer.showOnboardingStep(OnboardingStep.GoHome, this.main, [
            {x: this.buttonComplete.x, y: this.buttonComplete.y - this.buttonComplete.height * 0.8, d: "down"},
        ], () => {
            this.startIdleInterval();
        });
    }

    private newPostcard(cb?: Callback) {
        this.createPostcard();
        this.createBoard();
        const zIndex = this.board.zIndex;
        this.board.zIndex = 0;
        this.board.show(() => {
            this.board.zIndex = zIndex;
            if (cb) {
                cb();
            }
        });
    }

    private createPostcard() {
        if (this.postcard) {
            this.postcard.destroy();
        }
        this.postcard = new Postcard(this._screenData.location, this._screenData.level);
        this.postcard.onReveal.once(() => this.onRevealPostcard());
        this.postcard.onItemFact.add(() => this.onboardingPostcardComplete());
        this.postcardContainer.addChild(this.postcard);
        LocationPostcardLayout[this._screenData.location]![this._screenData.level]!.call(this);
        this.postcard.interactive = true;
        this.postcard.on("click", () => {
            for (const card of this.board.cards) {
                if (card.enabled && !card.isFlipping && !card.isShaking) {
                    card.pulse(2);
                }
            }
        });
    }

    private createBoard() {
        if (this.board) {
            this.board.destroy();
        }
        const totalCards = 14;
        const cardNumbers = (array((i) => i + MinCardNumber, MaxCardNumber) as CardNumber[])
            .filter((it) => !isCardExcluded(this._screenData.location, this._screenData.level, it));
        const cardTypes: LocationCardType[] = cardNumbers.map((it) => ({
            location: this._screenData.location,
            index: it,
            level: this._screenData.level,
        }));
        this.board = new Match2Board(new Match2Model<LocationCardType>(totalCards, cardTypes));
        this.postcardContainer.addChild(this.board);
        this.board.onMatch.add(this.onMatch2, this);
        this.board.onPickCard.add(() => {
            AudioChannel.SFX.play(Assets.audio.tileClick.assetName);
            this.startIdleInterval();
        });
        BoardCardsLayout_main.call(this);
    }

    private onMatch2(card1: Match2Card, card2: Match2Card) {
        const index = card1.cardType.index;
        const transform = this.postcard.getCardTransform(index);
        card1.pulse();
        card2.pulse(1, () => {
            card1.zIndex = -1;
            card2.zIndex = -1;
            transform.changeParent(this.board);
            const t = transform.getTransformBackup();
            const onMoveCards = Times(2, () => {
                this.postcard.revealPiece(index);
            });
            this.moveCard(card1, t, onMoveCards);
            this.moveCard(card2, t, onMoveCards);
            transform.changeParent(this.postcard);
            Keyboard.remove(card1, card2);
            Keyboard.focus(this.board.getClosestEnabledCard(card2) || this.buttonHome);
        });
        this.onboardingContainer.hideOnboardingStep();
        this.playMatch2VO(card1.cardType);
    }

    private playMatch2VO(cardType: LocationCardType) {
        const vo = [];
        const unmatched = this.board.model.unmatched;
        const tileVO = getLocationTileVO(cardType.location, cardType.level, cardType.index as CardNumber);
        const factVO = getLocationFactVO(cardType.location, cardType.level, cardType.index as CardNumber);
        if (tileVO) {
            vo.push(tileVO);
        }
        if (unmatched === 0) {
            let lunaDelay = 0;
            if (tileVO) {
                lunaDelay = CaptionManager.instance.getDuration(tileVO);
                vo.push(0.4);
            }
            this.postcard.delayedCall(lunaDelay, () => this.showLuna());
        }
        vo.push(HLF.ArrayUtils.random(voMatchKudos));
        if (factVO) {
            vo.push(0.25, factVO);
        }
        if (unmatched === 1) {
            vo.push(0.4, "patw_g06");
        }
        if (vo.length > 0) {
            VoiceOverManager.instance.playVO(vo, {
                ...voOptionsDirections,
                callback: () => {
                    this.startIdleInterval();
                    if (unmatched === 0) {
                        this._isMatchVoComplete = true;
                        this.onCompletePostcard();
                    }
                },
            });
        } else if (unmatched === 0) {
            this._isMatchVoComplete = true;
        }
    }

    private moveCard(card: Match2Card, destination: ITransformBackup, cb?: Callback) {
        const shadow = card.shadow;
        shadow.changeParent(card.parent);
        shadow.zIndex = card.zIndex;
        card.tweenTo(0.6, Ease.sineInOut, {
            ...destination,
            onComplete: () => {
                card.alphaTo(0, 0.2);
                if (cb) {
                    cb();
                }
            },
        });
        shadow.tweenTo(0.6, Ease.sineInOut, {
            ...destination,
            alpha: 0,
            onComplete: () => {
                shadow.changeParent(card);
                shadow.zIndex = 0;
            },
        });
    }

    private onRevealPostcard() {
        this._isPostcardRevealed = true;
        this.postcard.revealItems();
        this.postcard.sparkleItems();
        AudioChannel.SFX.play(Assets.audio.envelopeClick.assetName);
        this.onCompletePostcard();
    }

    private showLuna() {
        if (!this.luna) {
            this.luna = this.main.addChild(new Luna());
        }
        this.luna.idleBlink();
        GameScreenLayout_main.call(this);
        this.luna.moveYFrom(this.luna.y + this.luna.characterSpine.height, 0.4, Ease.backOut);
    }

    private onCompletePostcard() {
        if (this._isMatchVoComplete && this._isPostcardRevealed) {

            PersistentGameModel.completeLocationLevel(this._screenData.location, this._screenData.level);
            Analytics.event("endLevel", `${this._screenData.location}-${this._screenData.level}`);

            this.stopIdleInterval();
            this.buttonNext.visible = false;
            if (!Environment.isBPS()) {
                this._printablePostcard = new PrintablePostcard(this._screenData.location, this._screenData.level);
                this._printablePostcard.render(() => {
                    this._isPostcardRendered = true;
                    this.showSavePrint();
                });
            }
            this.showCompletePostcard();
        }
    }

    private showCompletePostcard(cb?: Callback) {
        this._isPostcardComplete = true;
        AudioChannel.SFX.play(Assets.audio.postcardComplete.assetName);
        this.postcard.interactive = false;
        this.postcard.tweenTo(0.6, Ease.sineInOut, {
            x: this.postcardEnd.x,
            y: this.postcardEnd.y,
            scaleX: this.postcardEnd.scaleX,
            scaleY: this.postcardEnd.scaleY,
            onComplete: () => {
                this.postcard.sparkleItems();
                AudioChannel.SFX.play(Assets.audio.envelopeClick.assetName);
            },
        });
        this.completePostcardContainer.visible = true;
        let d = 0;
        this.buttonComplete.enabled = false;
        this.buttonPrint.enabled = false;
        this.buttonSave.enabled = false;
        this.buttonHome.enabled = false;
        this.buttonHome.moveYTo(-this._size.y / 2 - this.buttonHome.height, 0.2, Ease.sineIn, {
            delay: d += 0.2,
            onComplete: () => this.buttonHome.visible = false,
        });
        if (Environment.isBPS()) {
            this.buttonSave.visible = false;
            this.buttonPrint.visible = false;
        } else {
            this.buttonSave.moveYFrom(this._size.y + this.buttonSave.height, 0.4, Ease.backOut, {delay: d += 0.2});
            this.buttonPrint.moveYFrom(this._size.y + this.buttonPrint.height, 0.4, Ease.backOut, {delay: d += 0.2});
        }
        this.buttonComplete.moveYFrom(this._size.y + this.buttonComplete.height, 0.4, Ease.backOut, {
            delay: d += 0.2,
            onComplete: () => {
                VoiceOverManager.instance.playVO([
                    HLF.ArrayUtils.random(voPostcardCompleteKudos),
                    HLF.ArrayUtils.random([
                        "patw_g08",
                        "patw_g09",
                        "patw_g10",
                        "patw_g11",
                    ]),
                ], {
                    ...voOptionsDirections,
                    callback: () => {
                        VoiceOverManager.instance.playVO("patw_g12", () => {
                            this.onboardingPostcardComplete();
                        });
                        this._voWatchers.patw_g12?.start();
                    },
                });
                this.buttonComplete.enabled = true;
                this.showSavePrint();
                this.postcard.enableItems();
                Keyboard.add(
                    this.buttonSave,
                    this.buttonPrint,
                    this.buttonComplete,
                    this.buttonMute,
                );
                if (cb) {
                    cb();
                }
            },
        });
        Keyboard.clear();
    }

    private showSavePrint() {
        if (this._isPostcardRendered && this._isPostcardComplete) {
            this.buttonSave.enabled = true;
            this.buttonPrint.enabled = true;
        }
    }

    private startIdleInterval() {
        this.stopIdleInterval();
        if (this._isPostcardRevealed) {
            this._idleVOComplete.start();
        } else if (this.board.model.unmatched === 1) {
            this._idleVOOneMath.start();
        } else {
            this._idleVO.start();
        }
    }

    private stopIdleInterval() {
        this._idleVO.stop();
        this._idleVOOneMath.stop();
        this._idleVOComplete.stop();
    }

    private randomClickUIStabilityTesting() {
        setInterval(() => {
            if (HLF.Random.bool()) {
                this.postcard.emit("click");
            } else {
                const card = HLF.ArrayUtils.random(this.board.cards.filter((it) => it.enabled));
                if (card && card.enabled) {
                    card.onClick(card);
                }
            }
        }, 100);
    }
}
